import { inject, Injectable } from '@angular/core';
import { MomentService, TRANSLATE_SERVICE } from '@luggagehero/shared/interfaces';
import moment from 'moment-timezone';

@Injectable({
  providedIn: 'root',
})
export class SharedMomentService implements MomentService {
  private translate = inject(TRANSLATE_SERVICE);

  private text = {
    TODAY: 'today',
    TOMORROW: 'tomorrow',
    YESTERDAY: 'yesterday',
    LAST: 'last',
    ON_DATE: 'on',
  };

  private dateTimeOptions: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  };
  private shortDateOptions: Intl.DateTimeFormatOptions = {
    year: '2-digit',
    month: '2-digit',
    day: '2-digit',
  };
  private shortestDateOptions: Intl.DateTimeFormatOptions = {
    year: undefined,
    month: '2-digit',
    day: '2-digit',
  };

  private get calendarFormats(): moment.CalendarSpec {
    return {
      sameDay: `[${this.text.TODAY.toLowerCase()}]`,
      nextDay: `[${this.text.TOMORROW.toLowerCase()}]`,
      nextWeek: 'dddd',
      lastDay: `[${this.text.YESTERDAY.toLowerCase()}]`,
      lastWeek: `[${this.text.LAST.toLowerCase()}] dddd`,
      sameElse: `MMM D`,
    };
  }

  private get onCalendarFormats(): moment.CalendarSpec {
    return {
      sameDay: `[${this.text.TODAY.toLowerCase()}]`,
      nextDay: `[${this.text.TOMORROW.toLowerCase()}]`,
      nextWeek: 'dddd',
      lastDay: `[${this.text.YESTERDAY.toLowerCase()}]`,
      lastWeek: `[${this.text.LAST.toLowerCase()}] dddd`,
      sameElse: `[${this.text.ON_DATE.toLowerCase()}] MMM D, YYYY`,
    };
  }

  private get calendarTimeFormats(): moment.CalendarSpec {
    return {
      sameDay: `LT [${this.text.TODAY.toLowerCase()}]`,
      nextDay: `LT [${this.text.TOMORROW.toLowerCase()}]`,
      nextWeek: 'LT dddd',
      lastDay: `LT [${this.text.YESTERDAY.toLowerCase()}]`,
      lastWeek: `LT [${this.text.LAST.toLowerCase()}] dddd`,
      sameElse: `LT MMM D, YYYY`,
    };
  }

  private get calendarTimeShortFormats(): moment.CalendarSpec {
    return {
      sameDay: `LT`,
      nextDay: `[${this.text.TOMORROW.toLowerCase()}] LT`,
      nextWeek: 'dddd LT',
      lastDay: `[${this.text.YESTERDAY.toLowerCase()}] LT`,
      lastWeek: `[${this.text.LAST.toLowerCase()}] dddd LT`,
      sameElse: `MMM D LT`,
    };
  }

  constructor() {
    this.translate.stream(this.text).subscribe((translatedText) => (this.text = translatedText));
  }

  formatDate(value: string | Date, format = '', timezone?: string): string {
    if (!value || value === '') {
      return '';
    }
    // Guard the language: moment's `.locale(undefined)` is a GETTER call that
    // returns the locale string, NOT the Moment — so the chain silently yields
    // an object without `.calendar`/`.toDate`, and every format below throws
    // "p.calendar is not a function". This bites whenever the translate service
    // has no current language yet — e.g. the landing app, which embeds this
    // wizard but never loads translations, so `currentLanguage` is undefined.
    // Fall back to English rather than passing undefined into `.locale()`.
    const m = moment(value).locale(this.translate.currentLanguage || 'en');

    if (timezone) {
      m.tz(timezone);
    }

    switch (format) {
      case 'calendar':
        return m.calendar(null, this.calendarFormats);

      case 'onCalendar':
        return m.calendar(null, this.onCalendarFormats);

      case 'calendarTime':
        return m.calendar(null, this.calendarTimeFormats);

      case 'calendarTimeShort':
        return m.calendar(null, this.calendarTimeShortFormats);

      // The native-Date branches only: compute it here, not unconditionally
      // above, so the calendar/fromNow/format paths never touch m.toDate().
      case 'localeDate':
        return m.toDate().toLocaleDateString();

      case 'localeDateShort':
        return m.toDate().toLocaleDateString([], this.shortDateOptions);

      case 'localeDateShortest':
        return m.toDate().toLocaleDateString([], this.shortestDateOptions);

      case 'localeDateTime':
        return m.toDate().toLocaleTimeString([], this.dateTimeOptions);

      case 'fromNow':
        return m.fromNow();

      default:
        return m.format(format);
    }
  }
}
